<template>
    <div>
        <label :for="`file_${uid}`">{{ label }}</label>
        <input
            type="text"
            hidden
            style="display: none"
        />
        <input
            :id="`file_${uid}`"
            ref="file"
            data-test-id="file-upload-input"
            type="file"
            :required="required"
            :accept="accept"
            @change="handleTempFileUpload()"
        />
        <progress-bar :percentage="uploadPercentage" />
    </div>
</template>
<script>
import axios from 'axios'
import { getCurrentInstance, markRaw } from 'vue'
import ProgressBar from './ProgressBar.vue'

export default {
    name: 'FileUpload',

    components: {
        ProgressBar,
    },

    props: {
        url: {
            required: true,
            type: String,
        },
        accept: {
            required: true,
            type: String,
        },
        required: {
            required: false,
            type: Boolean,
            default: true,
        },
        label: {
            required: false,
            type: String,
            default: 'File',
        },
    },

    emits: ['success', 'error', 'upload-started'],

    expose: ['abort'],

    data() {
        return {
            uploadPercentage: 0,
            temp_filename: null,
            original_filename: null,
            // markRaw keeps the native controller out of Vue's reactivity so
            // axios can attach listeners to the real AbortSignal
            abortController: null,
        }
    },

    computed: {
        uid() {
            return getCurrentInstance().uid
        },
    },

    methods: {
        // Cancel an upload that is still in flight. Intended to be called by a
        // parent through a ref, e.g. when a modal holding this field is closed.
        // No-op once the upload has finished or failed.
        abort() {
            if (!this.abortController) {
                return
            }

            this.abortController.abort()
            this.abortController = null

            this.resetUpload()
        },

        resetUpload() {
            this.temp_filename = null
            this.original_filename = null
            this.uploadPercentage = 0

            const fileInput = this.$refs.file

            if (!fileInput) {
                return
            }

            fileInput.type = 'text'
            fileInput.type = 'file'
        },

        handleTempFileUpload() {
            this.uploadPercentage = 0
            const file = this.$refs.file.files[0]
            const filename = file.name

            this.$emit('upload-started')

            this.abortController = markRaw(new AbortController())
            const { signal } = this.abortController

            this.$http
                .get(`${this.url}/upload-url?filename=${filename}`, { signal })
                .then((uploadUrlResponse) => {
                    const awsAxiosInstance = axios.create()
                    awsAxiosInstance.defaults.headers.common = {}

                    awsAxiosInstance
                        .put(uploadUrlResponse.data.url, file, {
                            headers: {
                                'Content-Type': 'multipart/form-data',
                            },
                            signal,
                            onUploadProgress: (progressEvent) => {
                                this.uploadPercentage = parseInt(
                                    Math.round((progressEvent.loaded * 100) / progressEvent.total)
                                )
                            },
                        })
                        .then(() => {
                            this.uploadPercentage = 100
                            this.abortController = null
                            this.$emit('success', {
                                temp: uploadUrlResponse.data.temp_filename,
                                original: filename,
                            })
                        })
                        .catch((error) => {
                            // the upload was cancelled by abort(), not by a failure
                            if (axios.isCancel(error)) {
                                return
                            }

                            this.resetUpload()

                            this.$emit('error', {
                                data: {
                                    errors: {
                                        file: ['Oops! Something went wrong.'],
                                    },
                                },
                            })
                        })
                })
                .catch((error) => {
                    // the upload was cancelled by abort(), not by a failure
                    if (axios.isCancel(error)) {
                        return
                    }

                    this.resetUpload()

                    if ('errors' in error.response.data) {
                        this.$emit('error', error.response)
                    } else {
                        this.$emit('error', {
                            data: { errors: { file: ['Oops! Something went wrong.'] } },
                        })
                    }
                })
        },
    },
}
</script>
